import { Add, Edit } from "@mui/icons-material";
import { Button, IconButton, Switch, Tooltip } from "@mui/material";
import { useFormik } from "formik";
import moment from "moment";
import { useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { API_URLS } from "../../config/APIUrls";
import axiosInstance from "../../config/axios";
import CustomDialog from "../../Shared/CustomDialogBox";
import CustomFilter from "../../Shared/CustomForFiler";
import CustomTable from "../../Shared/CustomTable";
import CustomToPagination from "../../Shared/CustomToPagination";

const LeaveMaster = () => {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [page, setPage] = useState(1);

    // Filter form
    const filterFormik = useFormik({
        initialValues: { search: "", start_date: "", end_date: "" },
        enableReinitialize: true,
    });

    // Get Leave list
    const { data: leaveData, isLoading: loading } = useQuery(
        ["get_leave", page, filterFormik.values],
        () =>
            axiosInstance.get(API_URLS.get_leave, {
                params: {
                    search: filterFormik.values.search,
                    start_date: filterFormik.values.start_date,
                    end_date: filterFormik.values.end_date,
                    page,
                    count: 10,
                },
            }),
        { refetchOnWindowFocus: false }
    );

    const leaveList = leaveData?.data?.response || [];

    // Formik for Add/Edit Leave Type
    const fk = useFormik({
        initialValues: {
            type: "",
            description: "N/A",
            status: "1",
        },
        onSubmit: async (values) => {
            if (editData) return updateLeave(values);
            return createLeave(values);
        },
    });

    // CREATE
    const createLeave = async (values) => {
        try {
            const reqBody = {
                m10_lv_type: values.type,
                m10_lv_desc: values.description,
                m10_lv_status: "1",
            };
            const res = await axiosInstance.post(API_URLS.create_leave, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_leave");
                handleClose();
            }
        } catch {
            toast.error("Failed to create leave type");
        }
    };

    // UPDATE
    const updateLeave = async (values) => {
        try {
            const reqBody = {
                m10_lv_id: editData.m10_lv_id,
                m10_lv_type: values.type,
                m10_lv_desc: values.description,
                m10_lv_status: values.status,
            };
            const res = await axiosInstance.post(API_URLS.update_leave, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) {
                queryClient.invalidateQueries("get_leave");
                handleClose();
            }
        } catch {
            toast.error("Failed to update leave type");
        }
    };

    // STATUS TOGGLE
    const toggleStatus = async (row, checked) => {
        try {
            const reqBody = { m10_lv_id: row.m10_lv_id, m10_lv_status: checked ? 1 : 0 };
            const res = await axiosInstance.post(API_URLS.status_leave, reqBody);
            toast(res?.data?.msg);
            if (res?.data?.success) queryClient.invalidateQueries("get_leave");
        } catch {
            toast.error("Failed to update status");
        }
    };

    const handleExport = async () => {
        try {
            const res = await axiosInstance.get(API_URLS.excel_leave, {
                search: filterFormik.values.search || "",
                start_date: filterFormik.values.start_date || "",
                end_date: filterFormik.values.end_date || "",
            })
            const fileUrl = res?.data?.response?.url;
            window.open(fileUrl, "_blank")
        }
        catch (e) {
            console.log("Something went wrong")
        }
    }

    // Dialog control
    const handleOpen = () => {
        fk.resetForm();
        setEditData(null);
        setOpen(true);
    };

    const handleEdit = (row) => {
        setEditData(row);
        fk.setValues({
            type: row.m10_lv_type,
            description: row.m10_lv_desc,
            status: String(row.m10_lv_status),
        });
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    // TABLE HEADERS
    const tableHead = ["S.No", "Leave Type", "Description", "Status", "Actions", "Created Date"];

    const tableRows = leaveList?.data?.map((item, index) => [
        index + 1 || "--",
        item.m10_lv_type || "--",
        item.m10_lv_desc || "--",
        <Switch
            checked={item.m10_lv_status === 1}
            onChange={(e) => toggleStatus(item, e.target.checked)}
            color="success"
        />,
        <Tooltip title="Edit">
            <IconButton onClick={() => handleEdit(item)}>
                <Edit className="text-blue-600" />
            </IconButton>
        </Tooltip>,
        moment(item.m10_lv_created_at).format("DD-MM-YYYY"),
    ]);

    return (
        <div className="mx-5">
            <div className="flex justify-between items-center p-1 ">
                <p className="font-bold text-xl">Leave Master</p>

                <Button variant="contained" startIcon={<Add />} onClick={handleOpen}>
                    Add Leave Type
                </Button>
            </div>

            <CustomFilter
                formik={filterFormik}
                onFilter={() => {
                    setPage(1);
                    queryClient.invalidateQueries("get_leave");
                }}
                onClear={() => {
                    filterFormik.resetForm();
                    setPage(1);
                    queryClient.invalidateQueries("get_leave");
                }}
                onExport={handleExport}
            />

            <CustomTable tablehead={tableHead} tablerow={tableRows} isLoading={loading} />

            <CustomToPagination setPage={setPage} page={page} data={leaveList} />

            <CustomDialog
                open={open}
                onClose={handleClose}
                onSubmit={fk.handleSubmit}
                title={editData ? "Edit Leave Type" : "Add Leave Type"}
                formik={fk}
                fields={[
                    { name: "type", label: "Leave Type" },
                    { name: "description", label: "Description" },
                ]}
            />
        </div>
    );
};

export default LeaveMaster;
